import {
  FindingPositionResolutionError,
} from './findingPositionResolver.js';

const DEFAULT_LOAD_TIMEOUT_MS = 15000;

function normalizePath(value) {
  if (!value) return '';
  try {
    return decodeURIComponent(new URL(value, 'http://local.invalid').pathname)
      .replace(/\\/g, '/')
      .toLowerCase();
  } catch (err) {
    const path = String(value).split('?')[0];
    try {
      return decodeURIComponent(path).replace(/\\/g, '/').toLowerCase();
    } catch (decodeErr) {
      return path.replace(/%2f/ig, '/').replace(/\\/g, '/').toLowerCase();
    }
  }
}

function resolutionError(code, message) {
  return new FindingPositionResolutionError(code, message);
}

function withTimeout(promise, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(
      () => reject(resolutionError(
        'POINT_CLOUD_LOAD_TIMEOUT',
        'The point cloud did not finish loading in time.',
      )),
      timeoutMs,
    );
    promise.then(
      value => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      err => {
        clearTimeout(timeoutId);
        reject(err);
      },
    );
  });
}

function selectPointCloud(image, pointClouds) {
  const imageId = image?.id == null ? null : String(image.id).toLowerCase();
  const canonicalSegment = imageId ? `/image360/${imageId}/potree/metadata.json` : null;
  const scanMatches = canonicalSegment
    ? pointClouds.filter(pointCloud => normalizePath(pointCloud?.url).includes(canonicalSegment))
    : [];

  if (scanMatches.length > 1) {
    throw resolutionError(
      'POINT_CLOUD_ASSOCIATION_AMBIGUOUS',
      'More than one point cloud matches this scan image.',
    );
  }
  if (scanMatches.length === 1) return scanMatches[0];

  const projectId = image?.forgeProjectId == null ? null : String(image.forgeProjectId);
  const projectMatches = projectId == null
    ? []
    : pointClouds.filter(pointCloud => pointCloud?.projectId != null
      && String(pointCloud.projectId) === projectId);
  if (projectMatches.length > 1) {
    throw resolutionError(
      'POINT_CLOUD_ASSOCIATION_AMBIGUOUS',
      'More than one point cloud matches this scan project.',
    );
  }
  if (projectMatches.length === 1) return projectMatches[0];

  throw resolutionError(
    'POINT_CLOUD_UNAVAILABLE',
    'No point cloud is explicitly associated with this scan.',
  );
}

export async function acquireFindingPointCloud({
  image,
  pointClouds = [],
  forgeService,
  onCleanupError = () => {},
  loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
}) {
  const target = selectPointCloud(image, pointClouds);
  const priorVisibility = new Map(pointClouds.map(pointCloud => [pointCloud, !!pointCloud.isShown]));
  const extension = forgeService.getMainExtension();
  let released = false;

  const restore = async () => {
    await extension.hideAllPointClouds();
    for (const pointCloud of pointClouds) {
      if (priorVisibility.get(pointCloud)) {
        await forgeService.setShowingPointCloudIn3D(pointCloud, true);
      }
    }
  };
  const safeRestore = async (originalError = null) => {
    try {
      await restore();
      return null;
    } catch (cleanupErr) {
      if (originalError) originalError.cleanupError = cleanupErr;
      onCleanupError(cleanupErr);
      return cleanupErr;
    }
  };

  let showPromise;
  try {
    await extension.hideAllPointClouds();
    showPromise = Promise.resolve(forgeService.setShowingPointCloudIn3D(target, true));
    await withTimeout(showPromise, loadTimeoutMs);
  } catch (err) {
    released = true;
    await safeRestore(err);
    if (showPromise && err?.code === 'POINT_CLOUD_LOAD_TIMEOUT') {
      const restoreLate = () => safeRestore();
      showPromise.then(restoreLate, restoreLate);
    }
    throw err;
  }

  return {
    pcName: target.name,
    async release() {
      if (released) return;
      released = true;
      return safeRestore();
    },
  };
}
